import { createContext, Fragment, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { EditLock, LockResource } from '@mcpanel/core';
import { rawRequest } from '../api/client';
import { clearEditSnapshot, registerEditGuard, resourceKey, setEditSession } from '../api/edit-sessions';
import { useApp, errorMessage } from '../app-context';

/**
 * 編集ロックの外（fieldset の下）に置く場所。依頼の承認の欄はここに出す。
 * 依頼を作ると編集ロックは「保存済み」で閉じて中のボタンが全部押せなくなるので、
 * 承認の欄を中に置くと「ワンタイムコードで承認」まで進めなくなる（1.8.0 の不具合）
 */
export const EditOutletContext = createContext<HTMLElement | null>(null);
const EditDirtyContext = createContext<((id: symbol, dirty: boolean) => void) | null>(null);

export function useEditDirty(dirty: boolean) {
  const update = useContext(EditDirtyContext);
  const id = useRef(Symbol());
  useEffect(() => {
    update?.(id.current, dirty);
    return () => update?.(id.current, false);
  }, [dirty, update]);
}

interface LockView {
  lock: EditLock | null;
  revision: string;
}
/**
 * 同じ対象へのロックの取得は 1 つずつ行う。作り直された画面（開発時の StrictMode や素早い画面の切り替え）で、
 * 前の取得がまだ返らないうちに次を送ると、前の取得のロックとぶつかって自分のロックに断られる
 */
const acquiring = new Map<string, Promise<unknown>>();
export function EditBoundary({
  resource,
  revision,
  children,
  automatic = true,
}: {
  resource: LockResource;
  revision?: string;
  children: ReactNode;
  automatic?: boolean;
}) {
  const { canOperate, me, notify } = useApp();
  const [lock, setLock] = useState<EditLock | null>();
  const [editing, setEditing] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState('');
  const [reset, setReset] = useState(0);
  const [dirty, setDirty] = useState(false);
  const changes = useRef(new Set<symbol>());
  const pending = useRef<Promise<void> | undefined>(undefined);
  const token = useRef<string>(undefined);
  const lifecycle = useRef(0);
  const key = resourceKey(resource);
  /**
   * 権限が変わる（me が届く）と下の useEffect で lifecycle を進め、取得中のロックを捨てる。
   * その effect が流れる前（描画が重いと次のタスクに回る）に入力を受けると、捨てられる取得を始めてしまうので、
   * effect が流れるまで入力欄と「引き継ぐ」を止めておく
   */
  const scope = `${key}:${canOperate}`;
  const [ready, setReady] = useState('');
  const settled = ready === scope;
  const baseline = useRef({ key, revision });
  if (baseline.current.key !== key) baseline.current = { key, revision };
  const acquire = (takeover = false): Promise<void> => {
    if (pending.current) return pending.current;
    if (token.current && !takeover) return Promise.resolve();
    const generation = lifecycle.current;
    const previous = acquiring.get(key);
    const task = (async () => {
      // 前の取得（と、要らなくなったときの返却）が終わるまで待つ
      if (previous) await previous.catch(() => undefined);
      if (generation !== lifecycle.current) throw new Error('編集を取り消しました');
      try {
        const view = await rawRequest<LockView>('POST', '/locks', { ...resource, takeover });
        if (generation !== lifecycle.current) {
          await rawRequest('POST', '/locks/release', { ...resource, token: view.lock!.token });
          throw new Error('編集を取り消しました');
        }
        token.current = view.lock!.token;
        setEditSession(resource, { token: token.current, revision: baseline.current.revision ?? view.revision });
        setLock(view.lock);
        setEditing(true);
        setConflict(false);
        setError('');
      } catch (err) {
        if (generation === lifecycle.current) {
          setError(errorMessage(err));
          const view = await rawRequest<LockView>('GET', `/locks?${new URLSearchParams(resource)}`).catch(
            (readError: unknown) => {
              notify(errorMessage(readError), true);
              return undefined;
            },
          );
          if (generation === lifecycle.current) setLock(view?.lock);
        }
        throw err;
      }
    })();
    pending.current = task;
    acquiring.set(key, task);
    void task
      .catch(() => undefined)
      .finally(() => {
        if (acquiring.get(key) === task) acquiring.delete(key);
      });
    void task
      .finally(() => {
        if (pending.current === task) pending.current = undefined;
      })
      .catch((err: unknown) => {
        if (generation === lifecycle.current) setError(errorMessage(err));
      });
    return task;
  };
  const acquireRef = useRef(acquire);
  acquireRef.current = acquire;
  const blocked = !canOperate || !settled || conflict || finished || Boolean(lock && lock.token !== token.current);
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;
  const begin = () => {
    const generation = lifecycle.current;
    void acquireRef.current().catch((err: unknown) => {
      if (generation === lifecycle.current) setError(errorMessage(err));
    });
  };
  const startEditing = () => {
    if (!blockedRef.current) begin();
  };
  const [updateDirty] = useState(() => (id: symbol, changed: boolean) => {
    if (changed) changes.current.add(id);
    else changes.current.delete(id);
    setDirty(changes.current.size > 0);
    if (changed) startEditing();
  });
  const releaseRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    let stopped = false;
    lifecycle.current++;
    setLock(undefined);
    setEditing(false);
    setConflict(false);
    setFinished(false);
    setError('');
    setReady(scope);
    const release = () => {
      const held = token.current;
      token.current = undefined;
      setEditSession(resource);
      if (held)
        void rawRequest('POST', '/locks/release', { ...resource, token: held }).catch((err: unknown) =>
          notify(errorMessage(err), true),
        );
    };
    releaseRef.current = release;
    const unregister = registerEditGuard(resource, async () => {
      if (blockedRef.current) throw new Error('この操作は現在できません');
      const generation = lifecycle.current;
      await acquireRef.current();
      if (generation !== lifecycle.current || !token.current || blockedRef.current)
        throw new Error('編集を取り消しました');
    });
    const refresh = async () => {
      const generation = lifecycle.current;
      const held = token.current;
      try {
        const view = held
          ? await rawRequest<LockView>('POST', '/locks', { ...resource, token: token.current })
          : await rawRequest<LockView>('GET', `/locks?${new URLSearchParams(resource)}`);
        if (!stopped && generation === lifecycle.current && token.current === held) setLock(view.lock);
      } catch (err) {
        if (stopped || generation !== lifecycle.current || token.current !== held) return;
        if (token.current) {
          release();
          setEditing(false);
          const view = await rawRequest<LockView>('GET', `/locks?${new URLSearchParams(resource)}`).catch(
            (readError: unknown) => {
              notify(errorMessage(readError), true);
              return undefined;
            },
          );
          if (!stopped && generation === lifecycle.current) {
            setLock(view?.lock);
            setError(view?.lock ? `${view.lock.name} が引き継ぎました` : errorMessage(err));
          }
        } else setError(errorMessage(err));
      }
    };
    const saved = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== key) return;
      release();
      setLock(null);
      setEditing(false);
      setFinished(true);
    };
    const collided = (event: Event) => {
      if ((event as CustomEvent<string>).detail === key) setConflict(true);
    };
    const unload = () => {
      lifecycle.current++;
      pending.current = undefined;
      release();
      setEditing(false);
      setLock(null);
    };
    window.addEventListener('edit-saved', saved);
    window.addEventListener('edit-conflict', collided);
    window.addEventListener('pagehide', unload);
    // 状態は上で戻したばかりなので、前の描画の blockedRef（ready 前）は見ずに始める
    if (automatic && resource.screen === 'approval' && canOperate) begin();
    else void refresh();
    const timer = setInterval(() => void refresh(), 30000);
    return () => {
      stopped = true;
      unregister();
      lifecycle.current++;
      pending.current = undefined;
      clearInterval(timer);
      window.removeEventListener('edit-saved', saved);
      window.removeEventListener('edit-conflict', collided);
      window.removeEventListener('pagehide', unload);
      release();
      clearEditSnapshot(resource);
    };
  }, [key, canOperate]);
  const other = lock && lock.token !== token.current;
  const [outlet, setOutlet] = useState<HTMLDivElement | null>(null);
  return (
    <div className="stack">
      {other && (
        <div className="alert warn">
          {lock.name}さんが{resource.screen === 'approval' ? '確認' : '編集'}中です（
          {Math.max(0, Math.floor((Date.now() - Date.parse(lock.since)) / 60000))} 分前から）
        </div>
      )}
      {error && <div className="alert warn">{error}</div>}
      {conflict && (
        <div className="alert warn">
          ほかの人が先に変更しました。
          <button className="btn" onClick={() => window.location.reload()}>
            読み込み直す
          </button>
        </div>
      )}
      {finished && (
        <button className="btn" onClick={() => window.location.reload()}>
          読み込み直す
        </button>
      )}
      {canOperate && settled && !editing && !conflict && !finished && (other || !automatic) && (
        <button
          className="btn"
          disabled={Boolean(other) && me?.user?.role !== 'owner'}
          onClick={() => {
            if (other && !window.confirm(`${lock.name} の編集ロックを引き継ぎますか？`)) return;
            void acquire(Boolean(other)).catch((err: unknown) => setError(errorMessage(err)));
          }}
        >
          {other ? '引き継ぐ' : '確認を始める'}
        </button>
      )}
      <fieldset
        disabled={blocked || (!automatic && !editing) || (resource.screen === 'approval' && !editing)}
        onChangeCapture={resource.screen !== 'approval' ? startEditing : undefined}
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      >
        <EditOutletContext.Provider value={outlet}>
          <EditDirtyContext.Provider value={updateDirty}>
            <Fragment key={`${key}:${reset}`}>{children}</Fragment>
          </EditDirtyContext.Provider>
        </EditOutletContext.Provider>
      </fieldset>
      {dirty && canOperate && !finished && resource.screen !== 'approval' && (
        <button
          className="btn"
          onClick={() => {
            lifecycle.current++;
            pending.current = undefined;
            const held = token.current;
            releaseRef.current();
            setLock((current) => (current?.token === held ? null : current));
            setEditing(false);
            setConflict(false);
            setError('');
            changes.current.clear();
            setDirty(false);
            setReset((value) => value + 1);
          }}
        >
          取り消し
        </button>
      )}
      <div ref={setOutlet} />
    </div>
  );
}
